'use client';

import { useState, useEffect, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { useControleSituacoes, useUpdateControleSituacoesMany } from '@/hooks/useControleSituacoes';
import { ControleSituacaoService } from '@/services/ControleSituacaoService';
import { ControleSituacao, ControleSituacaoUpdate } from '@/types/controle-situacoes';
import { SITUACAO_OPTIONS } from '@/lib/associado-options';
import { Loader2, Save, RefreshCw, AlertCircle, CheckCircle2, Plus } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { useToast } from '@/hooks/use-toast';
import { WarningAlert, InfoAlert, ErrorAlert } from '@/components/ui/alert';

export default function CadastroControle() {
  const { data: situacoes, isLoading, error, refetch } = useControleSituacoes();
  const updateManyMutation = useUpdateControleSituacoesMany();
  const { toast } = useToast();

  // Estado local para edição
  const [editedSituacoes, setEditedSituacoes] = useState<Record<string, ControleSituacao>>({});
  const [inputValues, setInputValues] = useState<
    Record<string, { valor_base_mensal: string; valor_por_dependente: string }>
  >({});
  const [hasChanges, setHasChanges] = useState(false);
  const [isCreating, setIsCreating] = useState(false);

  // Inicializar estado de edição quando os dados são carregados
  useEffect(() => {
    if (situacoes && situacoes.length > 0) {
      const initial: Record<string, ControleSituacao> = {};
      const initialInputs: Record<
        string,
        { valor_base_mensal: string; valor_por_dependente: string }
      > = {};
      situacoes.forEach((sit) => {
        initial[sit.id] = { ...sit };
        initialInputs[sit.id] = {
          valor_base_mensal: sit.valor_base_mensal.toString(),
          valor_por_dependente: sit.valor_por_dependente.toString(),
        };
      });
      setEditedSituacoes(initial);
      setInputValues(initialInputs);
      setHasChanges(false);
    }
  }, [situacoes]);

  // Validar se um valor é numérico positivo
  const validateValue = (value: string): number | null => {
    const numValue = parseFloat(value);
    if (isNaN(numValue) || numValue < 0) {
      return null;
    }
    return numValue;
  };

  // Atualizar valor na edição local
  const handleValueChange = (
    id: string,
    field: 'valor_base_mensal' | 'valor_por_dependente' | 'ativo',
    value: string | boolean
  ) => {
    if (field === 'ativo') {
      setEditedSituacoes((prev) => {
        const updated = { ...prev };
        if (updated[id]) {
          updated[id] = {
            ...updated[id],
            ativo: value as boolean,
          };
        }
        return updated;
      });
      setHasChanges(true);
      return;
    }

    // Atualizar o valor do input primeiro
    setInputValues((prev) => ({
      ...prev,
      [id]: {
        ...prev[id],
        [field]: value as string,
      },
    }));

    // Se o campo estiver vazio, manter o valor como 0
    if (value === '') {
      setEditedSituacoes((prev) => {
        const updated = { ...prev };
        if (updated[id]) {
          updated[id] = {
            ...updated[id],
            [field]: 0,
          };
        }
        return updated;
      });
      setHasChanges(true);
      return;
    }

    const numValue = validateValue(value as string);

    if (numValue === null) {
      // Valor inválido - não atualizar o valor numérico, apenas o input
      return;
    }

    setEditedSituacoes((prev) => {
      const updated = { ...prev };
      if (updated[id]) {
        updated[id] = {
          ...updated[id],
          [field]: numValue,
        };
      }
      return updated;
    });
    setHasChanges(true);
  };

  // Criar situações que não existem no banco
  const handleCreateMissing = async () => {
    if (!situacoes) return;

    setIsCreating(true);
    const situacoesExistentes = new Set(situacoes.map((s) => s.tipo_situacao));
    const situacoesParaCriar: Array<{
      tipo_situacao: string;
      valor_base_mensal: number;
      valor_por_dependente: number;
      ativo: boolean;
    }> = [];

    SITUACAO_OPTIONS.forEach((option) => {
      if (!situacoesExistentes.has(option.value)) {
        situacoesParaCriar.push({
          tipo_situacao: option.value,
          valor_base_mensal: 0,
          valor_por_dependente: 0,
          ativo: true,
        });
      }
    });

    if (situacoesParaCriar.length === 0) {
      toast({
        title: 'Nenhuma situação para criar',
        description: 'Todas as situações já estão cadastradas.',
      });
      setIsCreating(false);
      return;
    }

    try {
      for (const situacao of situacoesParaCriar) {
        await ControleSituacaoService.create(situacao);
      }
      toast({
        title: 'Sucesso',
        description: `${situacoesParaCriar.length} situação(ões) criada(s) com sucesso.`,
      });
      await refetch();
    } catch (error) {
      console.error('Erro ao criar situações:', error);
      toast({
        title: 'Erro',
        description: error instanceof Error ? error.message : 'Erro ao criar situações.',
        variant: 'destructive',
      });
    } finally {
      setIsCreating(false);
    }
  };

  // Salvar todas as alterações
  const handleSaveAll = async () => {
    if (!situacoes) return;

    const updates: Array<{ id: string; data: ControleSituacaoUpdate }> = [];

    situacoes.forEach((original) => {
      const edited = editedSituacoes[original.id];
      if (edited) {
        const hasChanges =
          edited.valor_base_mensal !== original.valor_base_mensal ||
          edited.valor_por_dependente !== original.valor_por_dependente ||
          edited.ativo !== original.ativo;

        if (hasChanges) {
          updates.push({
            id: original.id,
            data: {
              valor_base_mensal: edited.valor_base_mensal,
              valor_por_dependente: edited.valor_por_dependente,
              ativo: edited.ativo,
            },
          });
        }
      }
    });

    if (updates.length === 0) {
      toast({
        title: 'Nenhuma alteração',
        description: 'Não há alterações para salvar.',
      });
      return;
    }

    try {
      await updateManyMutation.mutateAsync(updates);
      setHasChanges(false);
      await refetch();
      // Toast já é mostrado pelo hook useUpdateControleSituacoesMany
    } catch (error) {
      console.error('Erro ao salvar:', error);
      // Toast de erro já é mostrado pelo hook useUpdateControleSituacoesMany
    }
  };

  // Recarregar dados (descartar alterações)
  const handleRefresh = () => {
    refetch();
    setHasChanges(false);
    if (situacoes) {
      const initial: Record<string, ControleSituacao> = {};
      const initialInputs: Record<
        string,
        { valor_base_mensal: string; valor_por_dependente: string }
      > = {};
      situacoes.forEach((sit) => {
        initial[sit.id] = { ...sit };
        initialInputs[sit.id] = {
          valor_base_mensal: sit.valor_base_mensal.toString(),
          valor_por_dependente: sit.valor_por_dependente.toString(),
        };
      });
      setEditedSituacoes(initial);
      setInputValues(initialInputs);
    }
  };

  // Garantir que todas as situações existam (mesmo que não estejam no banco)
  const allSituacoes = useMemo(() => {
    const situacoesMap = new Map(situacoes?.map((s) => [s.tipo_situacao, s]) || []);
    const all: ControleSituacao[] = [];

    SITUACAO_OPTIONS.forEach((option) => {
      const existing = situacoesMap.get(option.value);
      if (existing) {
        all.push(existing);
      } else {
        // Criar uma situação temporária para exibição
        all.push({
          id: `temp-${option.value}`,
          tipo_situacao: option.value,
          valor_base_mensal: 0,
          valor_por_dependente: 0,
          ativo: true,
        });
      }
    });

    return all;
  }, [situacoes]);

  // Verificar se há situações faltando
  const situacoesFaltando = useMemo(() => {
    if (!situacoes) return 0;
    const situacoesExistentes = new Set(situacoes.map((s) => s.tipo_situacao));
    return SITUACAO_OPTIONS.filter((option) => !situacoesExistentes.has(option.value)).length;
  }, [situacoes]);

  // Formatar valor monetário
  const formatCurrency = (value: number): string => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value);
  };

  if (error) {
    return (
      <div className="container mx-auto py-6">
        <Card>
          <CardContent className="pt-6">
            <ErrorAlert
              message={`Erro ao carregar dados: ${error instanceof Error ? error.message : 'Erro desconhecido'}`}
            />
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto py-6 space-y-6">
      {/* Botões de ação */}
      <div className="flex justify-end gap-2">
        <Button
          variant="outline"
          onClick={handleRefresh}
          disabled={isLoading || updateManyMutation.isPending || isCreating}
        >
          <RefreshCw className="h-4 w-4 mr-2" />
          Recarregar
        </Button>
        {situacoesFaltando > 0 && (
          <Button
            variant="outline"
            onClick={handleCreateMissing}
            disabled={isLoading || updateManyMutation.isPending || isCreating}
          >
            {isCreating ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Criando...
              </>
            ) : (
              <>
                <Plus className="h-4 w-4 mr-2" />
                Criar Situações Faltantes ({situacoesFaltando})
              </>
            )}
          </Button>
        )}
        <Button
          onClick={handleSaveAll}
          variant="success"
          disabled={!hasChanges || isLoading || updateManyMutation.isPending || isCreating}
        >
          {updateManyMutation.isPending ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Salvando...
            </>
          ) : (
            <>
              <Save className="h-4 w-4 mr-2" />
              Salvar Alterações
            </>
          )}
        </Button>
      </div>

      {/* Alerta de situações faltando */}
      {situacoesFaltando > 0 && (
        <InfoAlert
          message={`Existem ${situacoesFaltando} situação(ões) que ainda não foram cadastradas no banco de dados. Clique em "Criar Situações Faltantes" para criar automaticamente.`}
        />
      )}

      {/* Alerta de alterações não salvas */}
      {hasChanges && (
        <WarningAlert message="Você tem alterações não salvas. Clique em 'Salvar Alterações' para aplicar as mudanças." />
      )}

      {/* Tabela de Valores com design moderno */}
      <div className="relative bg-card rounded-xl border border-border overflow-hidden mb-20">
        {/* Table */}
        <style>{`
          [data-table-controle] table {
            border-collapse: collapse !important;
            width: 100%;
          }
          [data-table-controle] thead {
            background: hsl(var(--muted) / 0.3);
          }
          [data-table-controle] thead tr {
            border-bottom: 1px solid hsl(var(--border)) !important;
          }
          [data-table-controle] th {
            font-size: 11px !important;
            font-weight: 500 !important;
            text-transform: uppercase !important;
            letter-spacing: 0.05em !important;
            color: hsl(var(--muted-foreground)) !important;
            padding: 12px 16px !important;
            white-space: nowrap;
          }
          [data-table-controle] tbody tr {
            transition: background-color 0.15s ease;
          }
          [data-table-controle] tbody tr:hover {
            background: hsl(var(--muted) / 0.3);
          }
          [data-table-controle] tbody tr {
            border-bottom: 1px solid hsl(var(--border) / 0.5) !important;
          }
          [data-table-controle] tbody tr:last-child {
            border-bottom: none !important;
          }
          [data-table-controle] td {
            padding: 14px 16px !important;
            font-size: 13px;
            color: hsl(var(--foreground));
          }
          [data-table-controle] th,
          [data-table-controle] td {
            border-left: none !important;
            border-right: none !important;
          }
        `}</style>
        {isLoading ? (
          <div className="space-y-4 p-6">
            {[1, 2, 3, 4, 5].map((i) => (
              <Skeleton key={i} className="h-12 w-full" />
            ))}
          </div>
        ) : (
          <div className="overflow-x-auto pb-8" data-table-controle>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Situação</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Valor Base Mensal (R$)</TableHead>
                  <TableHead>Valor por Dependente (R$)</TableHead>
                  <TableHead>Total Estimado (Base + 1 Dep.)</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {allSituacoes.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center py-10">
                      <div className="flex flex-col items-center gap-2">
                        <AlertCircle className="h-8 w-8 text-muted-foreground" />
                        <p className="text-muted-foreground">Nenhuma situação encontrada.</p>
                      </div>
                    </TableCell>
                  </TableRow>
                ) : (
                  allSituacoes.map((situacao) => {
                    const edited = editedSituacoes[situacao.id] || situacao;
                    const inputVal = inputValues[situacao.id] || {
                      valor_base_mensal: edited.valor_base_mensal.toString(),
                      valor_por_dependente: edited.valor_por_dependente.toString(),
                    };
                    const totalEstimado = edited.valor_base_mensal + edited.valor_por_dependente;
                    const isTemporary = situacao.id.startsWith('temp-');

                    return (
                      <TableRow key={situacao.id}>
                        <TableCell className="font-medium">
                          {situacao.tipo_situacao}
                          {isTemporary && (
                            <Badge variant="outline" className="ml-2 text-xs">
                              Não cadastrado
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          {isTemporary ? (
                            <Badge variant="secondary" className="text-xs">
                              Criar primeiro
                            </Badge>
                          ) : (
                            <div className="flex items-center gap-2">
                              <Switch
                                checked={edited.ativo}
                                onCheckedChange={(checked) =>
                                  handleValueChange(situacao.id, 'ativo', checked)
                                }
                                disabled={updateManyMutation.isPending}
                                className="data-[state=checked]:bg-green-500 data-[state=checked]:hover:bg-green-600 data-[state=unchecked]:bg-red-400 data-[state=unchecked]:hover:bg-red-500"
                              />
                              <span
                                className={`text-sm font-medium ${edited.ativo
                                    ? 'text-green-600 dark:text-green-400'
                                    : 'text-red-600 dark:text-red-400'
                                  }`}
                              >
                                {edited.ativo ? 'Ativo' : 'Inativo'}
                              </span>
                            </div>
                          )}
                        </TableCell>
                        <TableCell>
                          {isTemporary ? (
                            <span className="text-sm text-muted-foreground">-</span>
                          ) : (
                            <div className="space-y-1">
                              <Input
                                type="number"
                                step="0.01"
                                min="0"
                                value={inputVal.valor_base_mensal}
                                onChange={(e) =>
                                  handleValueChange(
                                    situacao.id,
                                    'valor_base_mensal',
                                    e.target.value
                                  )
                                }
                                onBlur={(e) => {
                                  if (e.target.value === '') {
                                    handleValueChange(situacao.id, 'valor_base_mensal', '0');
                                  }
                                }}
                                className="w-full"
                                placeholder="0.00"
                                disabled={updateManyMutation.isPending}
                              />
                              <span className="text-xs text-muted-foreground">
                                {formatCurrency(edited.valor_base_mensal)}
                              </span>
                            </div>
                          )}
                        </TableCell>
                        <TableCell>
                          {isTemporary ? (
                            <span className="text-sm text-muted-foreground">-</span>
                          ) : (
                            <div className="space-y-1">
                              <Input
                                type="number"
                                step="0.01"
                                min="0"
                                value={inputVal.valor_por_dependente}
                                onChange={(e) =>
                                  handleValueChange(
                                    situacao.id,
                                    'valor_por_dependente',
                                    e.target.value
                                  )
                                }
                                onBlur={(e) => {
                                  if (e.target.value === '') {
                                    handleValueChange(situacao.id, 'valor_por_dependente', '0');
                                  }
                                }}
                                className="w-full"
                                placeholder="0.00"
                                disabled={updateManyMutation.isPending}
                              />
                              <span className="text-xs text-muted-foreground">
                                {formatCurrency(edited.valor_por_dependente)}
                              </span>
                            </div>
                          )}
                        </TableCell>
                        <TableCell>
                          {isTemporary ? (
                            <span className="text-sm text-muted-foreground">-</span>
                          ) : (
                            <div className="font-medium text-muted-foreground">
                              {formatCurrency(totalEstimado)}
                            </div>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </div>
        )}

        {/* Bottom decorative line */}
        <div className="absolute bottom-0 left-6 right-6 h-px bg-gradient-to-r from-transparent via-border to-transparent" />
      </div>
    </div>
  );
}
